/**
 * The course, in route order.
 *
 * Course-level problems are shown here rather than in the editor, because they
 * are properties of the set: two checkpoints sharing a route position is not a
 * fault of either one, and an organiser looking at a single checkpoint has no
 * way to see it.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import {
  effectiveMaxDwellMinutes,
  errors,
  validateCourse,
  warnings,
  type Checkpoint,
} from '@cairn/domain';

import { deleteCheckpoint, watchCheckpoints } from './data/checkpoints.js';
import { watchEvent } from './data/events.js';
import type { Membership } from './data/members.js';
import { ProblemList } from './ProblemList.js';

export function CheckpointList({
  membership,
}: {
  readonly membership: Membership;
}): React.JSX.Element {
  const [checkpoints, setCheckpoints] = useState<readonly Checkpoint[] | null>(null);
  const [eventDefaultDwell, setEventDefaultDwell] = useState<number | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const canEdit = membership.role === 'organiser';

  useEffect(() => {
    let cancelled = false;
    const stops: (() => void)[] = [];

    // Wrapped, always. An unhandled rejection here would leave the list on
    // "Loading…" for ever with nothing in the console — the step 06 lesson.
    void (async () => {
      try {
        const stopCheckpoints = await watchCheckpoints(membership.eventId, (list) => {
          if (!cancelled) setCheckpoints(list);
        });
        // The event is needed only for the dwell default, so that a checkpoint
        // inheriting it can show the number it will actually use rather than
        // the word "default", which tells an organiser nothing.
        const stopEvent = await watchEvent(membership.eventId, (event) => {
          if (!cancelled) setEventDefaultDwell(event?.defaultMaxDwellMinutes ?? null);
        });

        if (cancelled) {
          stopCheckpoints();
          stopEvent();
        } else {
          stops.push(stopCheckpoints, stopEvent);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setFailure(error instanceof Error ? error.message : String(error));
          setCheckpoints([]);
        }
      }
    })();

    return () => {
      cancelled = true;
      for (const stop of stops) stop();
    };
  }, [membership.eventId]);

  async function remove(id: string): Promise<void> {
    setFailure(null);
    try {
      await deleteCheckpoint(membership.eventId, id);
      setConfirming(null);
    } catch (error: unknown) {
      setFailure(error instanceof Error ? error.message : String(error));
    }
  }

  if (checkpoints === null) return <p>Loading the course…</p>;

  const problems = validateCourse(checkpoints);

  return (
    <section>
      <h2>Checkpoints</h2>

      {!canEdit && <p>You are signed in as {membership.role}. This is read-only.</p>}
      {failure !== null && <p role="alert">{failure}</p>}

      <ProblemList problems={errors(problems)} heading="Problems with the course" role="alert" />
      <ProblemList problems={warnings(problems)} heading="Worth checking" />

      {checkpoints.length === 0 ? (
        <p>No checkpoints yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Route</th>
              <th>No.</th>
              <th>Name</th>
              <th>Role</th>
              <th>Staffing</th>
              <th>Max dwell</th>
              <th>Signal</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {checkpoints.map((checkpoint) => (
              <tr key={checkpoint.id}>
                <td>{checkpoint.routeOrder}</td>
                <td>{checkpoint.number}</td>
                <td>
                  <Link to={`/checkpoints/${checkpoint.id}`}>{checkpoint.name}</Link>
                  {checkpoint.isSleepStop && ' — sleep stop'}
                </td>
                <td>{checkpoint.role}</td>
                <td>{checkpoint.staffingModel}</td>
                <td>{describeDwell(checkpoint, eventDefaultDwell)}</td>
                <td>{checkpoint.connectivity}</td>
                <td>
                  {canEdit &&
                    (confirming === checkpoint.id ? (
                      <>
                        <span>Delete {checkpoint.name}? </span>
                        <button type="button" onClick={() => void remove(checkpoint.id)}>
                          Yes, delete
                        </button>{' '}
                        <button
                          type="button"
                          onClick={() => {
                            setConfirming(null);
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setConfirming(checkpoint.id);
                        }}
                      >
                        Delete
                      </button>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canEdit && (
        <p>
          <Link to="/checkpoints/new">Add a checkpoint</Link>
        </p>
      )}
    </section>
  );
}

/**
 * Show the number that will actually apply, not the word "default".
 *
 * An organiser checking that Tan Hill will not alarm on sleeping runners needs
 * to see 480, or see that it is inheriting 60 — which is the mistake design
 * rule 12 is about.
 */
function describeDwell(checkpoint: Checkpoint, eventDefault: number | null): string {
  if (checkpoint.maxDwellMinutes !== null) return `${String(checkpoint.maxDwellMinutes)} min`;
  if (eventDefault === null) return 'event default';
  return `${String(eventDefault)} min (event default)`;
}
